/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react-hooks/immutability */
/**
 * Select components
 * Simple select components using native HTML select with Tailwind CSS styling.
 * Menu content is portaled to document.body so overflow:auto parents (e.g. node
 * config panel) cannot clip it or let clicks fall through to the canvas beneath.
 */

import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { isBugsnapUiEvent } from '@features/bug-report/bugsnapUi'
import './select.css'

function findScrollContainerBounds(trigger: HTMLElement): { containerTop: number; containerBottom: number } {
  let containerBottom = window.innerHeight
  let containerTop = 0
  let ancestor: HTMLElement | null = trigger.parentElement
  while (ancestor && ancestor !== document.body) {
    const st = window.getComputedStyle(ancestor)
    if (/(auto|scroll)/.test(`${st.overflowY}${st.overflow}`)) {
      const cr = ancestor.getBoundingClientRect()
      containerBottom = Math.min(containerBottom, cr.bottom)
      containerTop = Math.max(containerTop, cr.top)
      break
    }
    ancestor = ancestor.parentElement
  }
  return { containerTop, containerBottom }
}

function resolveSelectMenuZIndex(
  trigger: HTMLElement,
  baseZIndex: number,
  zIndexBump: number,
): number {
  let resolvedZ = baseZIndex
  let el: HTMLElement | null = trigger.parentElement
  while (el && el !== document.body) {
    const z = window.getComputedStyle(el).zIndex
    if (z && z !== 'auto') {
      const parsed = Number.parseInt(z, 10)
      if (!Number.isNaN(parsed) && parsed + zIndexBump > resolvedZ) {
        resolvedZ = parsed + zIndexBump
      }
    }
    el = el.parentElement
  }
  return resolvedZ
}

// Context for select state
interface SelectContextType {
  value: string
  onValueChange: (value: string) => void
  open: boolean
  setOpen: (open: boolean) => void
  triggerRef: React.RefObject<HTMLButtonElement | null>
  contentRef: React.RefObject<HTMLDivElement | null>
  menuStyle: React.CSSProperties | null
}

const SelectContext = createContext<SelectContextType | null>(null)

// Select Root
interface SelectProps {
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  children: React.ReactNode
  disabled?: boolean
}

export const Select: React.FC<SelectProps> = ({
  value: controlledValue,
  defaultValue = '',
  onValueChange,
  children,
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue)
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null)
  // True once the menu has been positioned from its real measured height rather
  // than the fallback guess used before it has ever mounted.
  const measuredRef = useRef(false)

  const value = controlledValue !== undefined ? controlledValue : internalValue

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (controlledValue === undefined) {
        setInternalValue(newValue)
      }
      onValueChange?.(newValue)
      setOpen(false)
    },
    [controlledValue, onValueChange]
  )

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const offset = 4
    const viewportPadding = 8
    const fallbackContentHeight = 240
    const minMenuHeight = 120
    const maxMenuHeight = 320
    const baseZIndex = 200
    const zIndexBump = 50

    const measured = contentRef.current?.scrollHeight
    const contentHeight = measured && measured > 0 ? measured : fallbackContentHeight

    const { containerTop, containerBottom } = findScrollContainerBounds(trigger)
    const spaceBelow = containerBottom - rect.bottom - viewportPadding
    const spaceAbove = rect.top - containerTop - viewportPadding
    const openUp = contentHeight + offset > spaceBelow && spaceAbove > spaceBelow
    const maxHeight = Math.max(
      minMenuHeight,
      Math.min(maxMenuHeight, openUp ? spaceAbove - offset : spaceBelow - offset)
    )
    const top = openUp
      ? Math.max(viewportPadding, rect.top - Math.min(contentHeight, maxHeight) - offset)
      : rect.bottom + offset

    setMenuStyle({
      position: 'fixed',
      top,
      left: Math.max(
        viewportPadding,
        Math.min(rect.left, window.innerWidth - rect.width - viewportPadding)
      ),
      width: rect.width,
      maxHeight,
      zIndex: resolveSelectMenuZIndex(trigger, baseZIndex, zIndexBump),
    })
  }, [])

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null)
      measuredRef.current = false
      return
    }
    updateMenuPosition()
    const handleReposition = () => updateMenuPosition()
    window.addEventListener('resize', handleReposition)
    const handleScrollClose = (event: Event) => {
      const target = event.target as Node
      if (
        !target ||
        target === document ||
        target === window ||
        !(target instanceof Node)
      ) {
        setOpen(false)
        return
      }
      if (contentRef.current && contentRef.current.contains(target)) {
        return
      }
      setOpen(false)
    }
    const scrollRoots = new Set<EventTarget>([window])
    let el: HTMLElement | null = triggerRef.current?.parentElement ?? null
    while (el) {
      const st = window.getComputedStyle(el)
      if (/(auto|scroll)/.test(`${st.overflowY}${st.overflowX}${st.overflow}`)) {
        scrollRoots.add(el)
      }
      el = el.parentElement
    }
    scrollRoots.forEach((target) => {
      target.addEventListener('scroll', handleScrollClose, true)
    })
    return () => {
      window.removeEventListener('resize', handleReposition)
      scrollRoots.forEach((target) => {
        target.removeEventListener('scroll', handleScrollClose, true)
      })
    }
  }, [open, updateMenuPosition])

  // The first pass above runs before the menu exists in the DOM, so it has to
  // guess the height to decide whether to flip open-upward — a guess that is
  // badly wrong for short menus and can throw them far above their trigger.
  // Re-measure and correct now that the menu has actually mounted. This runs
  // before paint, so the provisional position is never visible.
  useLayoutEffect(() => {
    if (!open || measuredRef.current || !menuStyle || !contentRef.current) return
    measuredRef.current = true
    updateMenuPosition()
  }, [open, menuStyle, updateMenuPosition])

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (event: MouseEvent) => {
      // Stay open while the user files a Bugsnap about this UI (see bugsnapUi).
      if (isBugsnapUiEvent(event.target, triggerRef.current)) return
      const target = event.target as Node
      if (triggerRef.current?.contains(target)) return
      if (contentRef.current?.contains(target)) return
      setOpen(false)
    }
    // Capture so we close before ReactFlow pane handlers see the event when
    // the user intentionally clicks the canvas — but do not stopPropagation
    // here (pane click should still dismiss the config panel).
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const contextValue = useMemo(
    () => ({
      value,
      onValueChange: handleValueChange,
      open,
      setOpen,
      triggerRef,
      contentRef,
      menuStyle,
    }),
    [value, handleValueChange, open, menuStyle]
  )

  return (
    <SelectContext.Provider value={contextValue}>
      <div className="ui-select-root">{children}</div>
    </SelectContext.Provider>
  )
}

// Select Trigger
interface SelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode
}

export const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const context = useContext(SelectContext)
    if (!context) throw new Error('SelectTrigger must be used within Select')

    const setRefs = (node: HTMLButtonElement | null) => {
      context.triggerRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    }

    return (
      <button
        ref={setRefs}
        type="button"
        className={cn('ui-select-trigger', className)}
        onClick={() => context.setOpen(!context.open)}
        aria-expanded={context.open}
        aria-haspopup="listbox"
        {...props}
      >
        {children}
        <ChevronDown
          className="ui-select-arrow"
          style={{ width: '16px', height: '16px', opacity: 0.5, transition: 'transform 0.2s', transform: context.open ? 'rotate(180deg)' : 'none' }}
        />
      </button>
    )
  }
)
SelectTrigger.displayName = 'SelectTrigger'

// Select Value
interface SelectValueProps {
  placeholder?: string
  children?: React.ReactNode
}

export const SelectValue: React.FC<SelectValueProps> = ({ placeholder, children }) => {
  const context = useContext(SelectContext)
  if (!context) throw new Error('SelectValue must be used within Select')

  return (
    <span
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        overflow: 'hidden',
        color: !context.value && !children ? 'var(--text-muted)' : 'inherit',
      }}
    >
      {children || context.value || placeholder}
    </span>
  )
}

// Select Content
interface SelectContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export const SelectContent = React.forwardRef<HTMLDivElement, SelectContentProps>(
  ({ className, children, style, onMouseDown, ...props }, ref) => {
    const context = useContext(SelectContext)

    if (!context) throw new Error('SelectContent must be used within Select')

    const setRefs = (node: HTMLDivElement | null) => {
      context.contentRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    }

    if (!context.open || !context.menuStyle) return null

    return createPortal(
      <div
        ref={setRefs}
        className={cn('ui-select-content', 'ui-select-content--portal', className)}
        style={{ ...context.menuStyle, ...style }}
        role="listbox"
        tabIndex={-1}
        onMouseDown={(e) => {
          // Keep the click on the portaled menu — do not let it fall through to
          // the ReactFlow pane (which would close the node config panel).
          e.stopPropagation()
          onMouseDown?.(e)
        }}
        {...props}
      >
        {children}
      </div>,
      document.body
    )
  }
)
SelectContent.displayName = 'SelectContent'

// Select Item
interface SelectItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string
  children: React.ReactNode
  /** When true, item is not selectable and shows muted styling. */
  disabled?: boolean
}

export const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ className, value, children, onMouseDown, disabled, title, ...props }, ref) => {
    const context = useContext(SelectContext)
    if (!context) throw new Error('SelectItem must be used within Select')

    const isSelected = context.value === value

    return (
      <div
        ref={ref}
        className={cn(
          'ui-select-item',
          isSelected && 'ui-select-item--selected',
          disabled && 'ui-select-item--disabled',
          className
        )}
        role="option"
        tabIndex={-1}
        aria-selected={isSelected}
        aria-disabled={disabled || undefined}
        title={title}
        // Use mousedown (not click) so selection runs before the menu unmounts
        // and the synthetic click cannot land on the canvas underneath.
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (disabled) return
          onMouseDown?.(e)
          context.onValueChange(value)
        }}
        {...props}
      >
        <div className="ui-select-item-content">{children}</div>
        {isSelected && <Check style={{ width: '16px', height: '16px', color: 'var(--primary, #007acc)', flexShrink: 0 }} />}
      </div>
    )
  }
)
SelectItem.displayName = 'SelectItem'
